import { useNavigate, useParams } from 'react-router-dom';
import { Column, Container, Header, Layout } from '@/components/SettingsLayout';
import { Text } from '@/ui';
import styles from '../styles.module.css';
import { OrganizationDashboard } from './OrganizationDashboard';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AccountCompany } from '../types';
import { accountsRef } from '../Accounts';
import { useGetUser } from '@/hooks';
import CompanySkeleton from '@/pages/companies/components/CompanySkeleton';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import { useGetOrganizationByIdQuery } from '@/store/slices/organizationsSlice';
import { skipToken } from '@reduxjs/toolkit/query';
import OrganizationProfile from './OrganizationProfile';
import OrgUsers from './OrgUsers';
import EditUserModal from './EditUserModal';
import { OrgApiKeys } from './OrgApiKeys';
import ImageWithFallback from '@/ui/ImageWithFallBack/ImageWithFallBack';
import SingleAccountList from './SingleAccountList';
import { useGetUsersQuery } from '@/store/slices/userApiSlice';
import FinalPricing from './FinalPricing';
import { CompanyTypes } from '@/types';
import OrganizationPricing from './OrganizationPricing';

export type AccountResponse = {
  name: string;
  companies: AccountCompany[] | [];
};

type Props = {
  baseUrl: string;
  backUrl?: string;
};
function SingleAccount({ baseUrl = accountsRef }: Props) {
  const { id, info } = useParams();
  const { t } = useTranslation();
  const { company } = useGetUser();
  const navigate = useNavigate();
  const { currentUser } = useGetUser();

  const {
    data: organization,
    isLoading,
    isError,
  } = useGetOrganizationByIdQuery(
    id && currentUser?.companyId ? { id, organizationId: currentUser.companyId } : skipToken,
  );

  const { data: usersData } = useGetUsersQuery(
    id
      ? {
          organizationId: id,
          filters: {
            limit: 100,
            offset: 0,
            isDeleted: false,
            companies: [id],
            organizationId: id,
          },
        }
      : skipToken,
  );

  const allowDeletion = (usersData?.data?.length || 0) > 1;

  useEffect(() => {
    if (!info) {
      navigate(`${baseUrl}/${id}/profile`);
    } else navigate(`${baseUrl}/${id}/${info}`);
  }, [info, navigate, id]);

  if (isError) throw new Error(t('Accounts.account_error'));

  if (isLoading) {
    return (
      <Layout>
        <Container>
          <SkeletonList />
        </Container>
        <Container size='lg'>
          <CompanySkeleton />
        </Container>
      </Layout>
    );
  }

  const isFinalPricing =
    info === 'pricing' && company?.type === CompanyTypes.ROOT && baseUrl !== accountsRef;

  const selfSettings = company?._id.toString() === id;
  return (
    <Layout>
      <Container>
        <Column gapSize='12'>
          <Header>
            <div className={styles.headerInfoHolder}>
              <div className={styles.headerText}>
                <Text size='ll' weight='semi-bold' color='base-blue' className='truncate'>
                  {organization?.name}
                </Text>
              </div>
            </div>
          </Header>
          <div className={styles.logoContainer}>
            <ImageWithFallback src={organization?.logo} />
          </div>
        </Column>
        <SingleAccountList baseUrl={baseUrl} selfSettings={selfSettings} />
      </Container>
      <Container size='lg'>
        {info === 'dashboard' && <OrganizationDashboard />}
        {info === 'profile' && <OrganizationProfile />}
        {/* {info === 'branding' && <Branding />} */}
        {info === 'users' && <OrgUsers />}
        {info === 'api-keys' && <OrgApiKeys />}
        {isFinalPricing && <FinalPricing />}
        {!isFinalPricing && info === 'pricing' && <OrganizationPricing />}
      </Container>

      <EditUserModal allowDeletion={allowDeletion} />
    </Layout>
  );
}

export default SingleAccount;
